import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CalendarDays, Calculator, Save } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { CoberturaMeses } from "@/components/CoberturaMeses";
import {
  calcularFacturacion,
  calendarioPotencia,
  facturarCel,
  facturarIrec,
  facturarPotencia,
  facturarPreliminar,
  getB2bPendientes,
  getContratos,
  getFacturas,
  getHistoricoConsumo,
  guardarFactura,
  guardarFacturaCel,
  guardarFacturaPotencia,
  guardarFacturaPreliminar,
  type CelFactura,
  type FacturaItem,
  type FacturacionResultado,
  type PotenciaCalendario,
  type LineaManual,
  type PotenciaFactura,
  type PreliminarFactura,
} from "@/lib/api";
import { ResumenFactura } from "./FacturasPage";
import FechaLimitePago from "@/components/FechaLimitePago";
import { ResumenCel } from "@/components/ResumenCel";
import { ManualesEditor } from "@/components/ManualesEditor";
import { ResumenPotencia } from "@/components/ResumenPotencia";

const num = (n: number, d = 2) =>
  new Intl.NumberFormat("es-MX", { minimumFractionDigits: d, maximumFractionDigits: d }).format(n);

// Producto a facturar (el radio de arriba). Los cuatro tienen motor: energía
// (heat-rate), potencia (parcialidades) y certificados (CEL / i-REC).
const PRODUCTOS = ["Energía", "Potencia", "CEL", "i-REC"] as const;
type Producto = (typeof PRODUCTOS)[number];

const addDays = (iso: string, n: number) => {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
};
const clampHasta = (inicio: string, fin: string | null) => {
  const def = addDays(inicio, 14);
  return fin && def > fin ? fin : def;
};

export function FacturacionPage() {
  const navigate = useNavigate();
  const contratosQ = useQuery({ queryKey: ["contratos"], queryFn: getContratos });
  // Producto a facturar: filtra el combo de contratos. Solo Energía tiene motor
  // de liquidación (heat-rate); Potencia y CEL se listan pero avisan que su
  // liquidación aún no existe en el ETRM.
  const [producto, setProducto] = useState<Producto>("Energía");

  // Oculta del combo los contratos cuya vigencia ya terminó (default: activo).
  const [soloVigentes, setSoloVigentes] = useState(true);
  const hoyIso = new Date().toISOString().slice(0, 10);
  const vigente = (c: { fecha_fin?: string | null }) => !c.fecha_fin || c.fecha_fin >= hoyIso;

  // Facturación solo para contratos donde MPP EMITE la TBFin (vendedor).
  const vendedores = (contratosQ.data?.items ?? []).filter((c) => c.rol_mpp === "vendedor");
  const delProducto = vendedores.filter((c) => (c.productos ?? []).includes(producto));
  const contratos = soloVigentes ? delProducto.filter(vigente) : delProducto;
  // Los radios se habilitan mirando TODOS los contratos (sin el filtro de
  // vigencia): si no, un producto sin contratos vigentes quedaría inalcanzable
  // y el usuario no podría llegar a desmarcar el filtro.
  const hayDe = (p: Producto) => vendedores.some((c) => (c.productos ?? []).includes(p));
  const ocultosPorVigencia = delProducto.length - contratos.length;

  const [contrato, setContrato] = useState("");
  const [desde, setDesde] = useState("2026-05-16");
  const [hasta, setHasta] = useState("2026-05-31");
  // Potencia y CEL se facturan por MES (Año-Mes), no por rango de días.
  const [ym, setYm] = useState(() => new Date().toISOString().slice(0, 7));
  const [resPot, setResPot] = useState<PotenciaFactura | null>(null);
  const [resCel, setResCel] = useState<CelFactura | null>(null);
  const [res, setRes] = useState<FacturacionResultado | null>(null);
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Selecciona el primer contrato de la lista; si el elegido ya no está en ella
  // (p. ej. se cambió de producto), cae al primero disponible.
  const enLista = contratos.some((c) => String(c.id_contrato) === contrato);
  const contratoSel = enLista
    ? contrato
    : contratos[0] ? String(contratos[0].id_contrato) : "";
  const sel = contratos.find((c) => String(c.id_contrato) === contratoSel);
  // Energía se liquida día por día (heat-rate); Potencia por parcialidad mensual.
  // CEL aún no tiene motor.
  const porMes = producto !== "Energía";        // Potencia y CEL se facturan por Año-Mes
  const facturable = true;                      // los tres productos ya tienen motor

  // Límites del periodo = vigencia del contrato. Los inputs los aplican con
  // min/max, y además se recorta el valor actual al cambiar de contrato.
  const ymMin = sel?.fecha_inicio?.slice(0, 7);
  const ymMax = sel?.fecha_fin?.slice(0, 7);

  // Al cambiar de contrato, ajusta el rango por defecto a su vigencia (los date
  // pickers ya quedan limitados con min/max a [fecha_inicio, fecha_fin]).
  useEffect(() => {
    if (!sel?.fecha_inicio) return;
    setDesde(sel.fecha_inicio);
    setHasta(clampHasta(sel.fecha_inicio, sel.fecha_fin));
    // el mes elegido se recorta a la vigencia del contrato nuevo
    setYm((cur) => {
      const min = sel.fecha_inicio?.slice(0, 7);
      const max = sel.fecha_fin?.slice(0, 7);
      if (min && cur < min) return min;
      if (max && cur > max) return max;
      return cur;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contratoSel, sel?.fecha_inicio, sel?.fecha_fin]);

  // Calendario de pagos de la potencia: decide si se factura eligiendo una
  // parcialidad de la lista (montos irregulares pactados en la OC) o un Año-Mes
  // (cobros regulares, como el mensual de 006DEA/MPP).
  const calPotQ = useQuery({
    queryKey: ["calendario-potencia", contratoSel],
    queryFn: () => calendarioPotencia(contratoSel),
    enabled: !!contratoSel && producto === "Potencia",
    retry: false,
  });
  const calPot = producto === "Potencia" ? calPotQ.data : undefined;
  const porParcialidad = calPot?.modo === "explicito";
  const [nPago, setNPago] = useState<number | null>(null);
  // Parcialidad YA facturada en el ETRM: no se vuelve a emitir (mig 085).
  const yaEmitida =
    producto === "Potencia"
      ? (calPot?.parcialidades ?? []).find(
          (p) => (porParcialidad ? p.n === nPago : p.ym === ym),
        )?.factura_id ?? null
      : null;
  // Preliminar + ajuste por gas (contratos con prepago_gas, p. ej. 002MPP/ATR)
  const [preliminarOn, setPreliminarOn] = useState(false);
  const modoPreliminarActivo = producto === "Energía" && !!sel?.prepago_gas && preliminarOn;
  // Al cambiar de contrato/calendario, preselecciona la primera parcialidad
  // pendiente (o la última si ya se facturaron todas).
  useEffect(() => {
    const ps = calPot?.parcialidades ?? [];
    if (!ps.length) {
      setNPago(null);
      return;
    }
    setNPago((ps.find((p) => !p.facturada) ?? ps[ps.length - 1]).n);
  }, [calPot]);

  // Facturas guardadas del contrato: alimentan el semáforo de cobertura por
  // mes (Energía por rango de días; Potencia por parcialidad del calendario).
  const facturasQ = useQuery({
    queryKey: ["facturas", contratoSel],
    queryFn: () => getFacturas(contratoSel),
    enabled: !!contratoSel && (producto === "Energía" || producto === "Potencia"),
  });

  // Meses "AAAA-MM" del calendario de pagos de potencia: explícitos si la OC
  // los pacta; consecutivos desde el primer pago si es cadencia regular.
  const mesesCalPot = useMemo(() => {
    if (!calPot) return null;
    if (calPot.parcialidades.length > 0)
      return Array.from(new Set(calPot.parcialidades.map((p) => p.ym)));
    if (!calPot.primer_pago_ym || !calPot.num_parcialidades) return null;
    const [a0, m0] = calPot.primer_pago_ym.split("-").map(Number);
    return Array.from({ length: calPot.num_parcialidades }, (_, i) => {
      const t = a0 * 12 + (m0 - 1) + i;
      return `${Math.floor(t / 12)}-${String((t % 12) + 1).padStart(2, "0")}`;
    });
  }, [calPot]);

  const historicoQ = useQuery({
    queryKey: ["historico-consumo", contratoSel],
    queryFn: () => getHistoricoConsumo(contratoSel),
    enabled: !!contratoSel,
  });
  // Meses con TBFin liquidada SIN factura (bolita roja en el mes del semáforo).
  const pendQ = useQuery({
    queryKey: ["b2b-pendientes"],
    queryFn: getB2bPendientes,
    staleTime: 5 * 60 * 1000,
  });
  const mesesPendientes =
    pendQ.data?.facturacion.find((p) => p.folio === sel?.folio)?.meses_captura ?? [];
  // Un mes manejado con preliminar/ajuste NO se factura con el motor completo:
  // si el rango elegido toca una de esas facturas, el Calcular normal se bloquea.
  const prelimSolapada = producto === "Energía"
    ? (facturasQ.data ?? []).find(
        (f) => (f.tipo ?? "completa") !== "completa" && f.desde <= hasta && f.hasta >= desde)
    : undefined;
  const historico = (historicoQ.data ?? []).map((m) => ({
    ...m,
    etiqueta: new Date(m.mes + "T00:00:00").toLocaleDateString("es-MX", {
      month: "short",
      year: "2-digit",
    }),
  }));

  const calcular = async () => {
    setLoading(true);
    setError(null);
    setMsg(null);
    try {
      setRes(null);
      setResPot(null);
      setResCel(null);
      if (producto === "Potencia")
        setResPot(
          await facturarPotencia(
            contratoSel,
            porParcialidad && nPago != null ? { n_pago: nPago } : { ym },
          ),
        );
      else if (producto === "CEL") setResCel(await facturarCel(contratoSel, ym));
      else if (producto === "i-REC") setResCel(await facturarIrec(contratoSel, ym));
      else setRes(await calcularFacturacion(contratoSel, desde, hasta));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al calcular");
      setRes(null);
      setResPot(null);
      setResCel(null);
    } finally {
      setLoading(false);
    }
  };

  const qc = useQueryClient();
  // El staleTime global (30 s) dejaba el semáforo con la lista cacheada al
  // volver del detalle: invalidar las facturas al guardar lo refresca solo.
  const invalidarFacturas = () => {
    qc.invalidateQueries({ queryKey: ["facturas"] });
    qc.invalidateQueries({ queryKey: ["facturas-todas"] });
  };

  // Cargos/créditos MANUALES (concepto + monto): se capturan antes de guardar
  // y entran al subtotal antes de IVA. Se limpian al cambiar contrato/producto.
  const [manuales, setManuales] = useState<LineaManual[]>([]);
  useEffect(() => {
    setManuales([]);
  }, [contratoSel, producto]);

  // FECHA LÍMITE DE PAGO (mig 083): el backend la precalcula al calcular, con
  // el plazo de la OC contado desde el último día del periodo; aquí es
  // editable y se manda tal cual al guardar.
  const [fechaLimite, setFechaLimite] = useState("");
  const sugerida =
    res?.fecha_limite_pago ?? resPot?.fecha_limite_pago ?? resCel?.fecha_limite_pago ?? null;
  useEffect(() => {
    setFechaLimite(sugerida ?? "");
  }, [sugerida, res, resPot, resCel]);

  const guardar = async () => {
    setGuardando(true);
    setMsg(null);
    setError(null);
    try {
      const f = await guardarFactura(contratoSel, desde, hasta, manuales,
        fechaLimite || null);
      invalidarFacturas();
      // Al guardar, pasa al detalle de la factura (ahí están PDF y Enviar a Flamma).
      navigate(`/facturas/${f.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al guardar la factura");
      setGuardando(false);
    }
  };

  // Guardado de la PARCIALIDAD de potencia calculada (etapa 2, mig 077): misma
  // ruta que energía — persiste en etrm.factura y abre el detalle (PDF/Flamma).
  const [guardandoPot, setGuardandoPot] = useState(false);
  const guardarPotencia = async () => {
    if (!resPot) return;
    setGuardandoPot(true);
    setMsg(null);
    setError(null);
    try {
      const f = await guardarFacturaPotencia(contratoSel, resPot.ym, resPot.n_pago, manuales,
        fechaLimite || null);
      invalidarFacturas();
      navigate(`/facturas/${f.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al guardar la factura de potencia");
      setGuardandoPot(false);
    }
  };

  // Guardado de la factura de CERTIFICADOS (CEL / i-REC): misma ruta que
  // energía y potencia — persiste en etrm.factura y abre el detalle (PDF/Flamma).
  const [guardandoCel, setGuardandoCel] = useState(false);
  const guardarCel = async () => {
    if (!resCel) return;
    setGuardandoCel(true);
    setMsg(null);
    setError(null);
    try {
      const f = await guardarFacturaCel(
        contratoSel, resCel.ym, (resCel.producto ?? "CEL") as "CEL" | "i-REC", manuales,
        fechaLimite || null);
      invalidarFacturas();
      navigate(`/facturas/${f.id}`);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al guardar la factura de certificados");
      setGuardandoCel(false);
    }
  };

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-semibold text-slate-900">Cálculo de facturación</h1>
        <p className="text-sm text-slate-500 mt-1">
          Liquidación del periodo (quincena): Cargo por Energía + Tarifas Reguladas + Cargo Fijo + IVA.
          Reutiliza el cálculo de la TBFin (activación, strike) por cada día.
        </p>
      </div>

      <div className="bg-white border rounded-lg p-4 space-y-3">
        {/* Producto a facturar: filtra el combo de contratos de abajo. */}
        <div className="flex flex-wrap items-center gap-4">
          <span className="text-xs font-medium text-slate-600">Facturar</span>
          {PRODUCTOS.map((p) => {
            const disponible = hayDe(p);
            return (
              <label
                key={p}
                className={`inline-flex items-center gap-1.5 text-sm ${
                  disponible ? "cursor-pointer text-slate-700" : "text-slate-300 cursor-not-allowed"
                }`}
                title={disponible ? `Facturar ${p}` : `No hay contratos de venta con ${p}`}
              >
                <input
                  type="radio"
                  name="producto-facturar"
                  checked={producto === p}
                  disabled={!disponible}
                  onChange={() => {
                    setProducto(p);
                    setRes(null);
                    setError(null);
                    setMsg(null);
                  }}
                  className="w-4 h-4 accent-blue-600"
                />
                {p}
              </label>
            );
          })}
          <label className="inline-flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={soloVigentes}
              onChange={(e) => setSoloVigentes(e.target.checked)}
              className="w-4 h-4 accent-blue-600 cursor-pointer"
            />
            Mostrar solo contratos vigentes
          </label>
          <span className="text-[11px] text-slate-400">
            Mostrando <b>{contratos.length}</b> de {vendedores.length} contrato
            {vendedores.length === 1 ? "" : "s"} de venta
            {vendedores.length - contratos.length > 0 && (
              <>
                {" "}· {vendedores.length - contratos.length} filtrado
                {vendedores.length - contratos.length === 1 ? "" : "s"} (
                {vendedores.length - delProducto.length > 0 &&
                  `${vendedores.length - delProducto.length} de otro producto`}
                {vendedores.length - delProducto.length > 0 &&
                  soloVigentes && ocultosPorVigencia > 0 && ", "}
                {soloVigentes && ocultosPorVigencia > 0 &&
                  `${ocultosPorVigencia} vencido${ocultosPorVigencia === 1 ? "" : "s"}`}
                )
              </>
            )}
          </span>
        </div>
        <div className="flex flex-wrap items-end gap-3">
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Contrato</span>
          <select
            className="mt-1 border rounded px-2 py-1.5 text-sm w-72"
            value={contratoSel}
            onChange={(e) => setContrato(e.target.value)}
          >
            {contratos.map((c) => (
              <option key={c.id_contrato} value={c.id_contrato}>
                {c.folio} — {c.contraparte}
              </option>
            ))}
          </select>
        </label>
        {modoPreliminarActivo ? (
          <span className="text-xs text-slate-500 pb-2">
            El cálculo se hace en el panel <b>Preliminar y ajuste</b> de abajo
          </span>
        ) : porParcialidad ? (
          <span className="text-xs text-slate-500 pb-2">
            Se factura por parcialidad (elige una abajo)
          </span>
        ) : porMes ? (
          <label className="block">
            <span className="text-xs font-medium text-slate-600">Periodo (Año-Mes)</span>
            <input
              type="month"
              className="mt-1 border rounded px-2 py-1.5 text-sm"
              value={ym}
              onChange={(e) => {
                // el picker respeta min/max, pero escribiendo se puede salir
                const v = e.target.value;
                if (!v) return;
                setYm(ymMin && v < ymMin ? ymMin : ymMax && v > ymMax ? ymMax : v);
              }}
              min={ymMin}
              max={ymMax}
              title={ymMin ? `Vigencia del contrato: ${ymMin} a ${ymMax ?? "—"}` : undefined}
            />
          </label>
        ) : (
          <>
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Desde</span>
              <input
                type="date"
                className="mt-1 border rounded px-2 py-1.5 text-sm"
                value={desde}
                onChange={(e) => {
                  const v = e.target.value;
                  setDesde(v);
                  // "Hasta" arranca en la misma fecha: el usuario parte de ahí
                  // (y nunca se queda con un rango invertido)
                  if (v) setHasta(v);
                }}
                min={sel?.fecha_inicio ?? undefined}
                max={sel?.fecha_fin ?? undefined}
              />
            </label>
            <label className="block">
              <span className="text-xs font-medium text-slate-600">Hasta</span>
              <input
                type="date"
                className="mt-1 border rounded px-2 py-1.5 text-sm"
                value={hasta}
                onChange={(e) => setHasta(e.target.value)}
                min={desde || (sel?.fecha_inicio ?? undefined)}
                max={sel?.fecha_fin ?? undefined}
              />
            </label>
          </>
        )}
        {!modoPreliminarActivo && (
        <button
          type="button"
          onClick={calcular}
          disabled={loading || !contratoSel || !facturable
            || (porParcialidad && nPago == null) || !!prelimSolapada || !!yaEmitida}
          title={yaEmitida
            ? `Esta parcialidad ya está facturada (factura #${yaEmitida}): ábrela en `
              + "Facturas, o bórrala si necesitas rehacerla"
            : prelimSolapada
            ? `El periodo toca la factura ${prelimSolapada.tipo} #${prelimSolapada.id} `
              + `(${prelimSolapada.desde} a ${prelimSolapada.hasta}): ese mes se maneja `
              + "con 'Preliminar y ajuste', no con la factura completa"
            : facturable ? undefined : `La liquidación de ${producto} aún no está implementada`}
          className="inline-flex items-center gap-2 bg-blue-600 text-white rounded px-4 py-2 text-sm hover:bg-blue-700 disabled:opacity-50"
        >
          <Calculator className="w-4 h-4" />
          {loading
            ? "Calculando…"
            : porParcialidad
              ? `Facturar parcialidad ${nPago ?? ""}`
              : "Calcular"}
        </button>
        )}
        {yaEmitida && (
          <span className="text-xs text-amber-700 pb-2">
            Parcialidad ya facturada —{" "}
            <Link to={`/facturas/${yaEmitida}`} className="underline hover:text-amber-900">
              abrir factura #{yaEmitida}
            </Link>{" "}
            (bórrala si necesitas rehacerla)
          </span>
        )}
        {prelimSolapada && !modoPreliminarActivo && (
          <span className="text-xs text-amber-700 pb-2">
            El mes ya tiene factura {prelimSolapada.tipo} (#{prelimSolapada.id}) — usa
            "Preliminar y ajuste"
          </span>
        )}
        {sel?.fecha_inicio && (
          <span className="text-xs text-slate-400 pb-2">
            Vigencia: {sel.fecha_inicio} a {sel.fecha_fin ?? "—"}
          </span>
        )}

        {/* Preliminar + ajuste por gas: solo contratos cuya OC pacta prepago
            (§20) y ajuste al gas efectivo (§18) — ficha con prepago_gas. */}
        {producto === "Energía" && sel?.prepago_gas && (
          <label className="inline-flex items-center gap-1.5 text-sm text-slate-600 cursor-pointer select-none pb-2">
            <input
              type="checkbox"
              checked={preliminarOn}
              onChange={(e) => setPreliminarOn(e.target.checked)}
              className="w-4 h-4 accent-blue-600 cursor-pointer"
            />
            Preliminar y ajuste
            <span className="text-[11px] text-slate-400">(prepago con gas promedio + ajuste del mes operado)</span>
          </label>
        )}

        {/* Semáforo de cobertura mensual de facturas guardadas (solo Energía:
            Potencia/CEL facturan por parcialidad, no por rango de días). */}
        {/* Semáforo de POTENCIA: una celda por parcialidad del calendario —
            verde si su factura ya está guardada, rojo si falta. */}
        {sel && producto === "Potencia" && mesesCalPot && (
          <CoberturaMeses
            vigIni={`${mesesCalPot[0]}-01`}
            vigFin={`${mesesCalPot[mesesCalPot.length - 1]}-28`}
            periodos={(facturasQ.data ?? []).filter((f) => f.producto === "Potencia")}
            mesesCalendario={mesesCalPot}
            storageKey="facturacion-cal-anio"
            docLabel="facturas de potencia"
          />
        )}

        {sel && producto === "Energía" && (
          <CoberturaMeses
            vigIni={sel.fecha_inicio}
            vigFin={sel.fecha_fin}
            periodos={(facturasQ.data ?? []).filter(
              (f) => (f.producto ?? "Energía") === "Energía",
            )}
            storageKey="facturacion-cal-anio"
            docLabel="facturas guardadas"
            pendientes={mesesPendientes}
            tipos={sel.prepago_gas
              ? Object.fromEntries(
                  (facturasQ.data ?? []).map((f) => [f.desde.slice(0, 7), f.tipo ?? "completa"]))
              : undefined}
          />
        )}
        </div>
        {contratos.length === 0 && (
          <div className="text-sm text-slate-500 bg-slate-50 border rounded p-3">
            {ocultosPorVigencia > 0 ? (
              <>
                No hay contratos de venta de <b>{producto}</b> con vigencia abierta hoy
                ({hoyIso}). Desmarca <b>“Mostrar solo contratos vigentes”</b> para facturar
                los {ocultosPorVigencia} contrato{ocultosPorVigencia === 1 ? "" : "s"} ya
                vencido{ocultosPorVigencia === 1 ? "" : "s"}.
              </>
            ) : (
              <>No hay contratos de venta con {producto}.</>
            )}
          </div>
        )}
      </div>

      {producto === "Energía" && sel?.prepago_gas && preliminarOn && (
        <PanelPreliminar
          contrato={contratoSel}
          vigIni={sel?.fecha_inicio}
          vigFin={sel?.fecha_fin}
          facturas={facturasQ.data ?? []}
        />
      )}

      {producto === "Potencia" && calPot && (
        <CalendarioPot cal={calPot} sel={nPago} onSel={setNPago} />
      )}
      {producto === "Potencia" && calPotQ.isError && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded p-3 text-sm">
          No se pudo armar el calendario de pagos:{" "}
          {calPotQ.error instanceof Error ? calPotQ.error.message : "error"}
        </div>
      )}

      {resPot && !resPot.factura_id && (
        <>
          <FechaLimitePago
            valor={fechaLimite}
            onChange={setFechaLimite}
            sugerida={resPot.fecha_limite_pago}
            plazoDias={resPot.plazo_pago_dias}
            plazoHabiles={resPot.plazo_pago_habiles}
            hasta={finDeMesIso(resPot.ym)}
          />
          <ManualesEditor lineas={manuales} onChange={setManuales} moneda={resPot.moneda} />
        </>
      )}
      {resPot && (
        <>
          <ResumenPotencia
            r={resPot}
            onGuardar={resPot.factura_id ? undefined : guardarPotencia}
            guardando={guardandoPot}
          />
          {resPot.factura_id && (
            <div className="text-xs text-slate-500">
              Ya facturada:{" "}
              <Link to={`/facturas/${resPot.factura_id}`} className="text-blue-600 hover:underline">
                abrir factura #{resPot.factura_id}
              </Link>{" "}
              (ahí están el PDF y Enviar a Flamma).
            </div>
          )}
        </>
      )}
      {resCel && !resCel.factura_id && (
        <>
          <FechaLimitePago
            valor={fechaLimite}
            onChange={setFechaLimite}
            sugerida={resCel.fecha_limite_pago}
            plazoDias={resCel.plazo_pago_dias}
            plazoHabiles={resCel.plazo_pago_habiles}
            hasta={finDeMesIso(resCel.ym)}
          />
          <ManualesEditor lineas={manuales} onChange={setManuales} moneda={resCel.moneda} />
        </>
      )}
      {resCel && (
        <>
          <ResumenCel
            r={resCel}
            onGuardar={resCel.factura_id ? undefined : guardarCel}
            guardando={guardandoCel}
          />
          {resCel.factura_id && (
            <div className="text-xs text-slate-500">
              Ya facturada:{" "}
              <Link to={`/facturas/${resCel.factura_id}`} className="text-blue-600 hover:underline">
                abrir factura #{resCel.factura_id}
              </Link>{" "}
              (ahí están el PDF y Enviar a Flamma).
            </div>
          )}
        </>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">{error}</div>
      )}
      {msg && (
        <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 rounded p-3 text-sm">
          {msg}
        </div>
      )}

      {res && (
        <>
          <FechaLimitePago
            valor={fechaLimite}
            onChange={setFechaLimite}
            sugerida={res.fecha_limite_pago}
            plazoDias={res.plazo_pago_dias}
            plazoHabiles={res.plazo_pago_habiles}
            hasta={res.hasta}
          />
          <ManualesEditor lineas={manuales} onChange={setManuales} moneda={res.moneda} />
          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={guardar}
              disabled={guardando}
              className="inline-flex items-center gap-2 bg-emerald-600 text-white rounded px-4 py-2 text-sm hover:bg-emerald-700 disabled:opacity-50"
            >
              <Save className="w-4 h-4" /> {guardando ? "Guardando…" : "Guardar factura"}
            </button>
            <span className="text-xs text-slate-500">
              Al guardar pasarás al detalle de la factura (Generar PDF · Enviar a Flamma)
              {fechaLimite && ` — vence el ${fechaLimite}`}
              {manuales.length > 0 &&
                ` — incluye ${manuales.length} línea${manuales.length === 1 ? "" : "s"} manual${
                  manuales.length === 1 ? "" : "es"}`}
              .
            </span>
          </div>

          {/* Mismo detalle que en Facturas emitidas: conceptos y detalle diario
              expandibles. Las advertencias las pinta ResumenFactura (no duplicar). */}
          <ResumenFactura res={res} />
        </>
      )}

      {historico.length > 0 && (
        <div className="bg-white border rounded-lg p-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="font-medium text-slate-800 text-sm">
              Consumo histórico (MWh registrados por mes)
            </span>
            <span className="text-xs text-slate-400">de las TBFin del contrato</span>
          </div>
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={historico} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="etiqueta" tick={{ fontSize: 12 }} />
                <YAxis
                  tick={{ fontSize: 12 }}
                  width={56}
                  tickFormatter={(v) => new Intl.NumberFormat("es-MX").format(v as number)}
                />
                <Tooltip
                  formatter={(v: number) => [`${num(v)} MWh`, "Consumo"]}
                  labelFormatter={(l) => `Mes: ${l}`}
                />
                <Bar dataKey="mwh" name="MWh" fill="#2563eb" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}

/** Último día del mes AAAA-MM en ISO (el periodo desde el que corre el plazo). */
function finDeMesIso(ym: string): string {
  const [a, m] = ym.split("-").map(Number);
  return `${ym}-${String(new Date(a, m, 0).getDate()).padStart(2, "0")}`;
}

// ---------- Calendario de pagos de POTENCIA ---------------------------------
// Las OC no siempre pagan parcialidades iguales: hay anticipos fijos y pagos
// que reparten el saldo al conocerse el PNP. Cuando el calendario es explícito
// se elige la parcialidad; cuando el cobro es regular (mensual) se elige el mes.
/** Preliminar (prepago §20) y ajuste por gas (§18) — contratos prepago_gas.
 *  El gas promedio se precarga del cálculo y el usuario lo puede editar; el
 *  valor editado es el que entra al cálculo. */
function PanelPreliminar({
  contrato,
  vigIni,
  vigFin,
  facturas,
}: {
  contrato: string;
  vigIni?: string | null;
  vigFin?: string | null;
  facturas: FacturaItem[];
}) {
  const ymMin = vigIni?.slice(0, 7);
  const ymMax = vigFin?.slice(0, 7);
  const clampYm = (v: string) => (ymMin && v < ymMin ? ymMin : ymMax && v > ymMax ? ymMax : v);
  const ymDefault = (m: "preliminar" | "ajuste") => {
    const hoy = new Date();
    const d = new Date(hoy.getFullYear(), hoy.getMonth() + (m === "preliminar" ? 1 : -1), 15);
    return clampYm(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
  };
  const ymMas = (v: string, delta: number) => {
    const [a, m] = v.split("-").map(Number);
    const i = a * 12 + (m - 1) + delta;
    return `${Math.floor(i / 12)}-${String((i % 12) + 1).padStart(2, "0")}`;
  };
  const [modo, setModo] = useState<"preliminar" | "ajuste">("preliminar");
  const [ym, setYm] = useState(() => ymDefault("preliminar"));
  const [gasStr, setGasStr] = useState("");           // editable; "" = usar promedio
  const [gasPrelimStr, setGasPrelimStr] = useState(""); // solo ajuste
  // Mes donde cae el DINERO en /b2b/utilidad: la preliminar se cobra ANTES del
  // mes de operación y el ajuste DESPUÉS. Editable; sin clamp a la vigencia
  // (el pago puede caer fuera de ella).
  const [mesFlujo, setMesFlujo] = useState(() => ymMas(ymDefault("preliminar"), -1));
  useEffect(() => {
    setMesFlujo(ymMas(ym, modo === "preliminar" ? -1 : 1));
  }, [modo, ym]);
  const [res, setRes] = useState<PreliminarFactura | null>(null);
  const [cargando, setCargando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const qc = useQueryClient();

  // Factura guardada que cubre el mes elegido (decide qué procede):
  //   preliminar → solo el ajuste; ajuste → mes cerrado; completa → nada.
  const finDeMes = (v: string) => {
    const [a, m] = v.split("-").map(Number);
    return `${v}-${String(new Date(a, m, 0).getDate()).padStart(2, "0")}`;
  };
  const facMes = facturas.find((f) => f.desde <= finDeMes(ym) && f.hasta >= `${ym}-01`);
  const tipoMes = facMes ? (facMes.tipo ?? "completa") : null;
  const bloqueo: string | null =
    modo === "preliminar"
      ? tipoMes === "preliminar"
        ? `Ya hay factura preliminar guardada de este mes (#${facMes!.id}): solo procede el ajuste.`
        : tipoMes === "ajuste"
          ? `Este mes ya está ajustado y facturado (#${facMes!.id}).`
          : tipoMes === "completa"
            ? `Este mes ya tiene una factura completa (#${facMes!.id}): no procede la preliminar.`
            : null
      : tipoMes == null
        ? "No hay factura preliminar guardada de este mes: guarda primero la preliminar."
        : tipoMes === "ajuste"
          ? `Este mes ya está ajustado y facturado (#${facMes!.id}).`
          : tipoMes === "completa"
            ? `Este mes tiene una factura completa (#${facMes!.id}): no procede el ajuste.`
            : null;
  // El gas de la preliminar viene de la factura GUARDADA (no se teclea)
  const gasPrelimDe =
    modo === "ajuste" && tipoMes === "preliminar" && facMes?.gas_usado != null
      ? facMes.gas_usado
      : null;
  useEffect(() => {
    if (gasPrelimDe != null) setGasPrelimStr(String(gasPrelimDe));
  }, [gasPrelimDe]);

  // Fecha del FIX a usar como TC (pedido 2026-09-02): vacío = regla automática
  // (último día hábil bancario del mes operado / hoy).
  const [tcFecha, setTcFecha] = useState("");
  // Cargos/créditos manuales de la preliminar/ajuste (entran al subtotal del mes)
  const [manualesPre, setManualesPre] = useState<LineaManual[]>([]);
  // Fecha límite de pago (mig 083): precalculada por el backend con el plazo
  // de la OC desde el fin del mes de operación; editable antes de guardar.
  // En el AJUSTE son dos documentos: el ajuste y la preliminar que corrige,
  // cada uno con su propio vencimiento (mig 084).
  const [fechaLimite, setFechaLimite] = useState("");
  const [fechaLimitePrel, setFechaLimitePrel] = useState("");

  const pedir = async (gas: number | null, gasPrelim: number | null) => {
    setCargando(true);
    setError(null);
    try {
      const r = await facturarPreliminar(contrato, ym, modo, gas, gasPrelim,
        tcFecha || null);
      setRes(r);
      setFechaLimite(r.fecha_limite_pago ?? "");
      setFechaLimitePrel(r.fecha_limite_pago_preliminar ?? "");
      // precarga el promedio calculado en el input (editable) si está vacío
      if (gas == null) setGasStr(String(r.gas_promedio));
      return r;
    } catch (e) {
      setError(e instanceof Error ? e.message : "Error al calcular");
      setRes(null);
    } finally {
      setCargando(false);
    }
  };

  // Al abrir el panel o cambiar modo/mes/contrato: cálculo automático con el
  // promedio (sin override), que además precarga el input del gas.
  useEffect(() => {
    setGasStr("");
    setRes(null);
    setError(null);
    if (bloqueo) return;  // estado inválido: el banner explica qué procede
    void pedir(null, modo === "ajuste"
      ? (gasPrelimDe ?? (gasPrelimStr ? Number(gasPrelimStr) : null))
      : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contrato, modo, ym, bloqueo, gasPrelimDe]);

  const gasNum = gasStr !== "" && !Number.isNaN(Number(gasStr)) ? Number(gasStr) : null;
  const gasPrelimNum =
    gasPrelimStr !== "" && !Number.isNaN(Number(gasPrelimStr)) ? Number(gasPrelimStr) : null;
  const money = (n: number) => `$${num(n)}`;
  // Los manuales entran al subtotal ANTES de IVA (misma aritmética que el
  // guardado en el backend): el resumen los refleja en vivo para que el TOTAL
  // en pantalla sea el que se va a guardar.
  const r2 = (n: number) => Math.round(n * 100) / 100;
  const extraMan = r2(manualesPre.reduce((s, l) => s + l.monto, 0));
  const subCon = res ? r2(res.subtotal + extraMan) : 0;
  const ivaCon = res ? r2(subCon * res.iva_pct) : 0;
  const totCon = res ? r2(subCon * (1 + res.iva_pct)) : 0;

  return (
    <div className="bg-white border rounded-lg p-4 space-y-3">
      <div className="flex flex-wrap items-end gap-3">
        <span className="text-sm font-semibold text-slate-800 pb-1.5">Preliminar y ajuste</span>
        {(["preliminar", "ajuste"] as const).map((m) => (
          <label key={m} className="inline-flex items-center gap-1.5 text-sm text-slate-700 cursor-pointer select-none pb-1.5">
            <input
              type="radio"
              name="modo-preliminar"
              checked={modo === m}
              onChange={() => {
                setModo(m);
                setYm(ymDefault(m));
              }}
              className="w-4 h-4 accent-blue-600"
            />
            {m === "preliminar" ? "Factura preliminar (prepago)" : "Ajuste del mes operado"}
          </label>
        ))}
        <label className="block">
          <span className="text-xs font-medium text-slate-600">Mes de operación</span>
          <input
            type="month"
            className="mt-1 border rounded px-2 py-1.5 text-sm"
            value={ym}
            onChange={(e) => e.target.value && setYm(clampYm(e.target.value))}
            min={ymMin}
            max={ymMax}
          />
        </label>
        <label className="block" title={res?.gas_detalle ?? undefined}>
          <span className="text-xs font-medium text-slate-600">
            {modo === "preliminar" ? "Gas promedio (editable, USD/MMBtu)" : "Gas efectivo del mes (editable)"}
          </span>
          <input
            type="number"
            step="0.0001"
            min="0"
            className="mt-1 border rounded px-2 py-1.5 text-sm w-36"
            value={gasStr}
            onChange={(e) => setGasStr(e.target.value)}
            placeholder={res ? String(res.gas_promedio) : "…"}
          />
        </label>
        {modo === "ajuste" && (
          <label className="block" title={gasPrelimDe != null
            ? `Tomado de la factura preliminar guardada #${facMes?.id}`
            : undefined}>
            <span className="text-xs font-medium text-slate-600">
              Gas usado en la preliminar
              {gasPrelimDe != null && (
                <span className="text-slate-400"> (de la factura #{facMes?.id})</span>
              )}
            </span>
            <input
              type="number"
              step="0.0001"
              min="0"
              className="mt-1 border rounded px-2 py-1.5 text-sm w-36 disabled:bg-slate-100 disabled:text-slate-500"
              value={gasPrelimStr}
              disabled={gasPrelimDe != null}
              onChange={(e) => setGasPrelimStr(e.target.value)}
              placeholder="guarda la preliminar"
            />
          </label>
        )}
        <label
          className="block"
          title="Mes donde cae el dinero en /b2b/utilidad (la preliminar se cobra antes del mes de operación; el ajuste después)"
        >
          <span className="text-xs font-medium text-slate-600">Afecta el mes (B2B)</span>
          <input
            type="month"
            className="mt-1 border rounded px-2 py-1.5 text-sm"
            value={mesFlujo}
            onChange={(e) => e.target.value && setMesFlujo(e.target.value)}
          />
        </label>
        <label
          className="block"
          title="El TC del cálculo será el FIX de Banxico de esta fecha (o del día hábil anterior con dato). Vacío = regla automática: último día hábil bancario del mes operado, u hoy si no ha cerrado."
        >
          <span className="text-xs font-medium text-slate-600">TC del día (FIX)</span>
          <input
            type="date"
            className="mt-1 border rounded px-2 py-1.5 text-sm"
            value={tcFecha}
            onChange={(e) => setTcFecha(e.target.value)}
          />
        </label>
        <button
          type="button"
          onClick={() => void pedir(gasNum, gasPrelimNum)}
          disabled={cargando || !ym || !!bloqueo || (modo === "ajuste" && gasPrelimNum == null)}
          className="inline-flex items-center gap-2 bg-blue-600 text-white rounded px-4 py-2 text-sm hover:bg-blue-700 disabled:opacity-50"
        >
          <Calculator className="w-4 h-4" />
          {cargando ? "Calculando…" : modo === "preliminar" ? "Calcular preliminar" : "Calcular ajuste"}
        </button>
        <button
          type="button"
          onClick={async () => {
            setGuardando(true);
            setError(null);
            try {
              const f = await guardarFacturaPreliminar(
                contrato, ym, modo, gasNum, gasPrelimNum, mesFlujo, tcFecha || null,
                manualesPre, fechaLimite || null,
                modo === "ajuste" ? fechaLimitePrel || null : null);
              // refresca el semáforo al volver (staleTime global de 30 s)
              qc.invalidateQueries({ queryKey: ["facturas"] });
              qc.invalidateQueries({ queryKey: ["facturas-todas"] });
              navigate(`/facturas/${f.id}`);
            } catch (e) {
              setError(e instanceof Error ? e.message : "Error al guardar");
              setGuardando(false);
            }
          }}
          disabled={
            guardando || cargando || !res || !!bloqueo ||
            (modo === "ajuste" && (gasPrelimNum == null || !res.tipo_ajuste))
          }
          title={modo === "ajuste"
            ? "Actualiza la factura del mes al valor final (CE al gas efectivo + CTreg); la diferencia queda en la memoria"
            : "Guarda la preliminar como la factura del mes (aparece en /facturas y en el semáforo)"}
          className="inline-flex items-center gap-2 bg-emerald-600 text-white rounded px-4 py-2 text-sm hover:bg-emerald-700 disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {guardando ? "Guardando…" : modo === "preliminar" ? "Guardar factura preliminar" : "Guardar ajuste"}
        </button>
      </div>
      {res && (
        <FechaLimitePago
          valor={fechaLimite}
          onChange={setFechaLimite}
          sugerida={res.fecha_limite_pago}
          plazoDias={res.plazo_pago_dias}
          plazoHabiles={res.plazo_pago_habiles}
          hasta={finDeMesIso(ym)}
          etiqueta={modo === "ajuste" ? "Fecha límite — ajuste" : "Fecha límite de pago"}
          valorPrevio={modo === "ajuste" ? fechaLimitePrel : undefined}
          onChangePrevio={modo === "ajuste" ? setFechaLimitePrel : undefined}
        />
      )}
      <ManualesEditor
        lineas={manualesPre}
        onChange={setManualesPre}
        moneda={res?.moneda ?? "USD"}
      />
      {bloqueo && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded p-3 text-sm">
          {bloqueo}
          {facMes && (
            <>
              {" "}
              <Link to={`/facturas/${facMes.id}`} className="text-blue-700 underline">
                Ver factura #{facMes.id} →
              </Link>
            </>
          )}
        </div>
      )}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded p-3 text-sm">{error}</div>
      )}
      {res && (
        <div className="grid md:grid-cols-2 gap-4">
          <table className="text-sm w-full">
            <tbody>
              <tr className="border-b">
                <td className="py-1.5 text-slate-500">Volumen (take or pay)</td>
                <td className="py-1.5 text-right tabular-nums">
                  {num(res.ee, 0)} MWh × {num(res.horas, 0)} h = {num(res.mwh_total, 0)} MWh
                </td>
              </tr>
              <tr className="border-b">
                <td className="py-1.5 text-slate-500">Gas usado (PUC = gas + {num(res.adder)})</td>
                <td className="py-1.5 text-right tabular-nums">
                  {num(res.gas_usado, 4)} → PUC {num(res.puc, 4)} USD/MMBtu
                </td>
              </tr>
              <tr className="border-b">
                <td className="py-1.5 text-slate-500">CE = MWh × (HR {num(res.hr, 1)} × PUC + CVOM {num(res.cvom, 0)})</td>
                <td className="py-1.5 text-right tabular-nums">{money(res.ce_m)}</td>
              </tr>
              <tr className="border-b">
                <td className="py-1.5 text-slate-500">
                  CTreg = MWh × TReg {num(res.treg_mxn_mwh)} / TC {num(res.tc, 4)}
                </td>
                <td className="py-1.5 text-right tabular-nums">{money(res.ctreg_m)}</td>
              </tr>
              {res.modo === "ajuste" && res.ce_preliminar != null && (
                <tr className="border-b">
                  <td className="py-1.5 text-slate-500">
                    CE preliminar (gas {num(res.gas_preliminar ?? 0, 4)})
                  </td>
                  <td className="py-1.5 text-right tabular-nums">{money(res.ce_preliminar)}</td>
                </tr>
              )}
              {res.modo === "ajuste" && res.ctreg_preliminar != null && (
                <tr className="border-b">
                  <td className="py-1.5 text-slate-500">
                    CTreg preliminar{res.tc_preliminar
                      ? ` (TC ${num(res.tc_preliminar, 4)})`
                      : ""}
                  </td>
                  <td className="py-1.5 text-right tabular-nums">{money(res.ctreg_preliminar)}</td>
                </tr>
              )}
            </tbody>
          </table>
          <table className="text-sm w-full">
            <tbody>
              {res.modo === "ajuste" && res.dif_ce != null && (
                <tr className="border-b">
                  <td className="py-1.5 text-slate-500">Diferencia por gas (CE real − CE preliminar)</td>
                  <td className="py-1.5 text-right tabular-nums">{money(res.dif_ce)}</td>
                </tr>
              )}
              {res.modo === "ajuste" && res.dif_ctreg != null && (
                <tr className="border-b">
                  <td className="py-1.5 text-slate-500">
                    Diferencia por tarifas reguladas (CTreg al TC del cierre)
                  </td>
                  <td className="py-1.5 text-right tabular-nums">{money(res.dif_ctreg)}</td>
                </tr>
              )}
              <tr className="border-b">
                <td className="py-1.5 text-slate-500">
                  {res.modo === "ajuste" ? "Diferencia del cierre (gas + tarifas reguladas)" : "Subtotal (CE + CTreg)"}
                </td>
                <td className="py-1.5 text-right tabular-nums">{money(res.subtotal)}</td>
              </tr>
              {extraMan !== 0 && (
                <tr className="border-b">
                  <td className="py-1.5 text-slate-500">
                    Cargos/créditos manuales ({manualesPre.length})
                  </td>
                  <td className="py-1.5 text-right tabular-nums">{money(extraMan)}</td>
                </tr>
              )}
              <tr className="border-b">
                <td className="py-1.5 text-slate-500">IVA ({num(res.iva_pct * 100, 0)}%)</td>
                <td className="py-1.5 text-right tabular-nums">{money(ivaCon)}</td>
              </tr>
              <tr className="border-b bg-emerald-50">
                <td className="py-1.5 font-semibold">
                  {res.modo === "ajuste" ? "TOTAL del ajuste" : "TOTAL preliminar"} ({res.moneda})
                </td>
                <td className="py-1.5 text-right tabular-nums font-semibold">{money(totCon)}</td>
              </tr>
              {res.tipo_ajuste && (
                <tr>
                  <td colSpan={2} className="py-1.5">
                    <span className={`text-[12px] font-medium rounded-full px-2 py-0.5 ${
                      res.tipo_ajuste.includes("favor de MPP")
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-amber-100 text-amber-700"
                    }`}>
                      {res.tipo_ajuste}
                    </span>
                  </td>
                </tr>
              )}
              <tr>
                <td colSpan={2} className="py-1.5 text-[11px] text-slate-400">{res.gas_detalle}</td>
              </tr>
            </tbody>
          </table>
          {res.advertencias.length > 0 && (
            <div className="md:col-span-2 bg-amber-50 border border-amber-200 text-amber-800 rounded p-2.5 text-xs space-y-0.5">
              {res.advertencias.map((a, i) => (
                <div key={i}>⚠ {a}</div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CalendarioPot({
  cal,
  sel,
  onSel,
}: {
  cal: PotenciaCalendario;
  sel: number | null;
  onSel: (n: number) => void;
}) {
  const money = (n: number, d = 2) =>
    new Intl.NumberFormat("es-MX", {
      style: "currency",
      currency: cal.moneda === "USD" ? "USD" : "MXN",
      minimumFractionDigits: d,
      maximumFractionDigits: d,
    }).format(n);
  const facturadas = cal.parcialidades.filter((p) => p.facturada).length;
  const TIPO: Record<string, string> = {
    fijo: "Monto fijo",
    residual: "Saldo / partes",
    ajuste: "Ajuste por PNP",
  };

  return (
    <div className="bg-white border rounded-lg overflow-hidden">
      <div className="px-4 py-3 border-b bg-slate-50 flex flex-wrap items-center gap-x-3 gap-y-1">
        <CalendarDays className="w-4 h-4 text-slate-500" />
        <span className="font-medium text-slate-800">Calendario de pagos</span>
        <span className="text-xs text-slate-500">
          {cal.pc_mw_ano} MW-año × {money(cal.precio_unitario, 4)} = <b>{money(cal.cargo_total)}</b>
          {cal.modo === "explicito" ? (
            <>
              {" · "}
              <b>
                {facturadas} de {cal.num_parcialidades}
              </b>{" "}
              facturada{facturadas === 1 ? "" : "s"}
            </>
          ) : (
            <>
              {" · "}
              {cal.pagos_por_ano
                ? `cobro mensual (${cal.pagos_por_ano}/año)`
                : `${cal.num_parcialidades} parcialidades iguales`}
              {cal.primer_pago_ym && ` · ${cal.primer_pago_ym} a ${cal.ultimo_pago_ym}`}
            </>
          )}
        </span>
        {cal.pnp_mw_ano != null && (
          <span className="text-[11px] text-slate-400">
            PNP AP{cal.pnp_anio_produccion} = {num(cal.pnp_mw_ano, 2)} × {cal.factor_pnp}
            {cal.pnp_version && ` (CENACE ${cal.pnp_version})`}
          </span>
        )}
      </div>

      {cal.modo === "periodo" ? (
        <div className="px-4 py-3 text-sm text-slate-600">
          Este contrato se factura eligiendo el <b>Periodo (Año-Mes)</b> arriba: cada mes
          se cobra {num(cal.pc_mw_ano / (cal.pagos_por_ano ?? cal.num_parcialidades), 4)} MAW
          ×{" "}
          {money(cal.precio_unitario)} ={" "}
          <b>
            {money(
              (cal.pc_mw_ano / (cal.pagos_por_ano ?? cal.num_parcialidades)) *
                cal.precio_unitario,
            )}
          </b>
          .
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-500 border-b">
                  <th className="px-3 py-2 w-8"></th>
                  <th className="px-3 py-2">Pago</th>
                  <th className="px-3 py-2">Mes</th>
                  <th className="px-3 py-2">Tipo</th>
                  <th className="px-3 py-2 text-right">Cantidad</th>
                  <th className="px-3 py-2 text-right">Valor unitario</th>
                  <th className="px-3 py-2 text-right">Importe</th>
                  <th className="px-3 py-2">CFDI emitido</th>
                  <th className="px-3 py-2">Factura ETRM</th>
                </tr>
              </thead>
              <tbody>
                {cal.parcialidades.map((p) => {
                  const dif =
                    p.cfdi_importe != null ? p.cfdi_importe - p.monto : null;
                  return (
                    <tr
                      key={p.n}
                      onClick={() => onSel(p.n)}
                      className={`border-b cursor-pointer ${
                        sel === p.n ? "bg-blue-50" : "hover:bg-slate-50"
                      }`}
                    >
                      <td className="px-3 py-2">
                        <input
                          type="radio"
                          name="parcialidad-pot"
                          checked={sel === p.n}
                          onChange={() => onSel(p.n)}
                          className="w-4 h-4 accent-blue-600"
                        />
                      </td>
                      <td className="px-3 py-2 font-medium">{p.n}</td>
                      <td className="px-3 py-2 tabular-nums">{p.ym}</td>
                      <td className="px-3 py-2 text-slate-600">
                        {TIPO[p.tipo] ?? p.tipo}
                        {p.monto < 0 && (
                          <span className="ml-1 text-[10px] bg-rose-100 text-rose-700 rounded px-1 py-0.5">
                            nota de crédito
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums">{num(p.cantidad, 4)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">
                        {money(p.precio_unitario)}
                      </td>
                      <td className="px-3 py-2 text-right tabular-nums font-medium">
                        {money(p.monto)}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {p.facturada ? (
                          <span className="text-emerald-700">
                            ✓ {p.cfdi_folio}
                            {p.cfdi_fecha && ` · ${p.cfdi_fecha}`}
                            {dif != null && Math.abs(dif) > 0.05 && (
                              <span className="text-amber-700"> ({num(dif, 2)})</span>
                            )}
                          </span>
                        ) : (
                          <span className="text-slate-400">pendiente</span>
                        )}
                      </td>
                      {/* Factura guardada en el ETRM: bloquea re-facturar (mig 085) */}
                      <td className="px-3 py-2 text-xs">
                        {p.factura_id ? (
                          <Link
                            to={`/facturas/${p.factura_id}`}
                            onClick={(e) => e.stopPropagation()}
                            className="text-blue-600 hover:underline"
                          >
                            #{p.factura_id}
                          </Link>
                        ) : (
                          <span className="text-slate-300">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-slate-50 font-medium">
                  <td colSpan={6} className="px-3 py-2 text-right text-xs text-slate-500">
                    Facturado {money(cal.facturado)} · Pendiente {money(cal.pendiente)}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums">{money(cal.cargo_total)}</td>
                  <td></td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
          {cal.parcialidades.some((p) => p.nota) && (
            <div className="border-t px-4 py-2 text-[11px] text-slate-500 space-y-0.5">
              {cal.parcialidades
                .filter((p) => p.nota)
                .map((p) => (
                  <div key={p.n}>
                    Pago {p.n}: {p.nota}
                  </div>
                ))}
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---------- Factura de POTENCIA (parcialidad del mes) -----------------------
// Replica el concepto único del CFDI real: cantidad en MAW × precio por MW-año.
// (ResumenPotencia vive en components/ResumenPotencia.tsx — compartido con el
// detalle de /facturas desde la etapa 2 de facturación de potencia.)
